import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { isEmbedded } from '@/lib/embed'

// Session store. Sign-in happens on /login against qtz-account, and AuthGuard
// holds the app until it has. One documented bypass survives that:
//   - VITE_API_TOKEN: a Bearer token supplied by the environment, for
//     deployments that front the portal with their own auth.
// Values learned at runtime (org name, user info, refreshed tokens) persist on
// top of those defaults.

interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  expiresAt: string | null
  userId: string | null
  orgId: string | null
  orgName: string | null
  orgPlan: string | null
  // Organisation emblem shown in the sidebar workspace block, when supplied.
  orgLogo: string | null
  userEmail: string | null
  role: string | null
  isAuthenticated: boolean

  setSession: (params: {
    accessToken: string
    refreshToken: string
    expiresAt: string
    userId: string
    orgId: string
    role?: string
  }) => void
  setOrgInfo: (orgName: string, orgPlan?: string, orgLogo?: string | null) => void
  setUserInfo: (email: string, role: string) => void
  clearSession: () => void
  updateTokens: (accessToken: string, refreshToken: string, expiresAt: string) => void
  // Embed mode: session handed over by the partner parent (access token only, no refresh token).
  applyAccessToken: (accessToken: string, expiresAt: string) => void
}

// The built-in demo tenant was withdrawn; a session persisted against it is
// signed out on load (see merge below) rather than left on data that no
// longer exists.
const RETIRED_DEMO_ORG_ID = 'demo-org'
const ENV_ORG_ID = import.meta.env.VITE_ORG_ID || null
const ENV_API_TOKEN = import.meta.env.VITE_API_TOKEN || null

const DEFAULT_SESSION = {
  accessToken: ENV_API_TOKEN,
  refreshToken: null,
  expiresAt: null,
  userId: null,
  orgId: ENV_ORG_ID,
  orgName: null,
  orgPlan: null,
  orgLogo: null,
  userEmail: null,
  role: null,
  // An environment-supplied token stands in for a sign-in; everything else
  // starts signed out and goes through /login.
  isAuthenticated: !!ENV_API_TOKEN,
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      ...DEFAULT_SESSION,

      setSession: ({ accessToken, refreshToken, expiresAt, userId, orgId, role }) =>
        set({
          accessToken,
          refreshToken,
          expiresAt,
          userId,
          orgId,
          role: role ?? null,
          isAuthenticated: true,
        }),

      setOrgInfo: (orgName, orgPlan, orgLogo) => set({ orgName, orgPlan: orgPlan ?? null, orgLogo: orgLogo ?? null }),

      setUserInfo: (email, role) => set({ userEmail: email, role }),

      // Back to the environment defaults (used on sign-out and when a token refresh fails).
      clearSession: () => set({ ...DEFAULT_SESSION }),

      updateTokens: (accessToken, refreshToken, expiresAt) =>
        set({ accessToken, refreshToken, expiresAt }),

      applyAccessToken: (accessToken, expiresAt) => {
        const claims = parseJwtClaims(accessToken)
        set((s) => {
          // Different user or org → drop cached profile so Layout refetches it.
          const sameUser = s.userId === claims.uid && s.orgId === claims.oid
          return {
            accessToken,
            refreshToken: null,
            expiresAt,
            userId: claims.uid,
            orgId: claims.oid,
            role: claims.role ?? null,
            isAuthenticated: true,
            ...(sameUser ? {} : { orgName: null, orgPlan: null, userEmail: null }),
          }
        })
      },
    }),
    {
      name: 'qtz-readiness-auth',
      // Embedded sessions are per-tab and must never mix with a top-level login.
      storage: createJSONStorage(() => (isEmbedded() ? sessionStorage : localStorage)),
      // A previously persisted null must not hide an env default — only
      // non-null persisted values override it.
      merge: (persisted, current) => {
        const saved = (persisted ?? {}) as Partial<AuthState>
        const next: AuthState = { ...current }
        for (const key of Object.keys(DEFAULT_SESSION) as (keyof typeof DEFAULT_SESSION)[]) {
          const v = saved[key]
          if (v !== null && v !== undefined) next[key] = v as never
        }
        if (ENV_ORG_ID) next.orgId = ENV_ORG_ID
        // A session left over from the withdrawn demo tenant: back to signed out.
        if (next.orgId === RETIRED_DEMO_ORG_ID) {
          next.orgId = DEFAULT_SESSION.orgId
          next.orgName = null
          next.isAuthenticated = DEFAULT_SESSION.isAuthenticated
        }
        if (ENV_API_TOKEN && !saved.refreshToken) {
          next.accessToken = ENV_API_TOKEN
          next.isAuthenticated = true
        }
        return next
      },
    }
  )
)

// ── JWT claim extraction ───────────────────────────────────────────────────────

interface JwtClaims {
  uid: string
  oid: string
  role?: string
}

export function parseJwtClaims(token: string): JwtClaims {
  const payload = token.split('.')[1]
  return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as JwtClaims
}
